import { useEffect, useState } from 'react';
import classNames from 'classnames';
import { PorangeButton } from "@/components/atoms/PorangeButton";

export const Question = ({
    data,
    onAnswer,
    flowVersion,
    previousAnswer,
    isTransitioning,
    direction,
    onTransitionComplete,
    skipAnswerAnimation = false,
    isInSlider = false,
    locationData,
}) => {
    const [selectedAnswers, setSelectedAnswers] = useState(() => {
        if (!previousAnswer) return [];
        return Array.isArray(previousAnswer) ? previousAnswer : [previousAnswer];
    });
    const [showContent, setShowContent] = useState(false);
    const [isAnimating, setIsAnimating] = useState(false);
    
    useEffect(() => {
        setShowContent(false);
        
        if (!isTransitioning) {
            setTimeout(() => setShowContent(true), 150);
        } else {
            setShowContent(false);
        }
    }, [data, isTransitioning, previousAnswer]);
    
    const handleOptionClick = (option, event) => {
        if (isTransitioning || isAnimating) return;
        
        if (data.isMultipleChoice) {
            setSelectedAnswers(prev => {
                const validPrev = prev.filter(ans => ans && ans.text);
                const isSelected = validPrev.some(ans => ans.id === option.id);
                
                if (option.text.match(/^(None|No)(\s|$)/i)) {
                    return isSelected ? [] : [option];
                }
                
                const withoutNoneOptions = validPrev.filter(ans => !ans.text.match(/^(None|No)(\s|$)/i));
                
                if (!isSelected && data.maxSelections && withoutNoneOptions.length >= data.maxSelections) {
                    return withoutNoneOptions;
                }
                
                return isSelected
                    ? withoutNoneOptions.filter(ans => ans.id !== option.id)
                    : [...withoutNoneOptions, option];
            });
        } else {
            setSelectedAnswers([option]);
            onAnswer(option, event);
        }
    };
    
    const handleContinue = (event) => {
        if (isTransitioning || isAnimating) return;
        onAnswer(selectedAnswers);
    };
    
    const formatAnswerText = (text) => {
        if (!locationData) return text;
        
        return text
            .replace('{neighborhood}', locationData.neighborhood || '')
            .replace('{city}', locationData.city || '')
            .replace('{region}', locationData.region || '')
            .replace('{country}', locationData.country || '');
    };
    
    // First, create the filteredAnswers with the existing logic
    const getFilteredAnswers = () => {
        const validAnswers = data.answers.filter(answer => {
            const formattedText = formatAnswerText(answer.text);
            
            if (!formattedText?.trim()) return false;
            
            if (data.type === 'WhereDoYouWantPlaces') {
                switch (answer.id) {
                    case 'neighborhood':
                        return !!locationData?.neighborhood;
                    case 'city':
                        return !!locationData?.city;
                    case 'region':
                        return !!locationData?.region;
                    case 'country':
                        return !!locationData?.country;
                    case 'worldwide':
                        return true;
                    default:
                        return true;
                }
            }
            
            return !formattedText.includes('{') && !formattedText.includes('}');
        });
        
        // Remove duplicates based on formatted text
        const seen = new Set();
        return validAnswers.filter(answer => {
            const formattedText = formatAnswerText(answer.text).toLowerCase().trim();
            if (seen.has(formattedText)) {
                return false;
            }
            seen.add(formattedText);
            return true;
        });
    };
    
    const filteredAnswers = getFilteredAnswers();
    
    return (
        <div className={classNames(
            "w-full",
            { "pointer-events-none": isTransitioning || isAnimating }
        )}>
            <div className={classNames(
                "w-full pb-6",
                {
                    "pointer-events-none": isTransitioning || isAnimating,
                    "transition-all duration-500 ease-in-out": isTransitioning,
                    "translate-x-full opacity-0": isTransitioning && direction === 'back',
                    "-translate-x-full opacity-0": isTransitioning && direction === 'forward',
                }
            )}>
                <div className="px-5 pt-6 pb-1">
                    <h2
                        className={classNames(
                            "text-2xl text-gray-700 font-semibold text-center whitespace-pre-line",
                            "transition-all duration-500 ease-out",
                            {
                                "opacity-0 translate-y-4": !showContent,
                                "opacity-100 translate-y-0": showContent,
                                "mb-6": !data.subtext,
                                "mb-2": data.subtext
                            }
                        )}
                    >
                        {data.text}
                    </h2>
                    {data.subtext && (
                        <p
                            className={classNames(
                                "text-base text-gray-500 text-center mb-5",
                                "transition-all duration-500 ease-out",
                                {
                                    "opacity-0 translate-y-4": !showContent,
                                    "opacity-100 translate-y-0": showContent
                                }
                            )}
                        >
                            {data.subtext}
                        </p>
                    )}
                </div>
                <div className="px-5 space-y-3 overflow-y-auto h-[49vh]">
                    {filteredAnswers.map((option, index) => (
                        <QuestionOption
                            key={option.id}
                            option={option}
                            index={index}
                            isSelected={selectedAnswers.some(ans => ans.id === option.id)}
                            onClick={(e) => handleOptionClick(option, e)}
                            showContent={showContent}
                            animationDelay={index * 100}
                            isMultipleChoice={data.isMultipleChoice}
                            locationData={locationData}
                            formatAnswerText={formatAnswerText}
                        />
                    ))}
                </div>
            </div>
            
            {data.isMultipleChoice && (
                <div className={classNames(
                    "fixed bottom-0 left-0 right-0 p-5",
                    "transition-all duration-500 ease-out",
                    {
                        "opacity-100 translate-y-0": selectedAnswers.length > 0 && showContent && !isTransitioning,
                        "opacity-0 translate-y-8 pointer-events-none": selectedAnswers.length === 0 || !showContent || isTransitioning
                    }
                )}>
                    <PorangeButton
                        full={true}
                        className="h-[60px] rounded-[20px] text-lg shadow-lg"
                        onClick={handleContinue}
                    >
                        Continue
                    </PorangeButton>
                </div>
            )}
        </div>
    );
};

const QuestionOption = ({
    option,
    isSelected,
    onClick,
    showContent,
    animationDelay,
    isMultipleChoice,
    locationData,
    formatAnswerText
}) => {
    const isNoneOption = option.text.match(/^(None|No)(\s|$)/i);
    
    return (
        <button
            onClick={(e) => onClick(e)}
            style={{
                transitionDelay: `${animationDelay}ms`
            }}
            className={classNames(
                "w-full px-5 py-4 text-left rounded-2xl mb-3",
                "transition-all duration-300 ease-out",
                "flex items-center gap-4",
                "border-[1.5px]",
                {
                    "opacity-0 translate-y-4": !showContent,
                    "opacity-100 translate-y-0": showContent,
                    "border-pink-500 bg-pink-50": isSelected,
                    "border-gray-200 bg-white": !isSelected
                }
            )}
        >
            {isMultipleChoice && !isNoneOption && (
                <div className={classNames(
                    "w-5 h-5 rounded-md",
                    "flex items-center justify-center",
                    "transition-all duration-300 ease-out",
                    "border-[1.5px]",
                    {
                        "border-pink-500 bg-pink-500": isSelected,
                        "border-gray-300": !isSelected
                    }
                )}>
                    {isSelected && (
                        <svg 
                            className="w-3.5 h-3.5 text-white" 
                            viewBox="0 0 20 20"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                        >
                            <path 
                                d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" 
                                fill="currentColor"
                            />
                        </svg>
                    )}
                </div>
            )}
            <span className="font-medium text-[15px] text-gray-900 flex-1">
                {formatAnswerText(option.text)}
            </span>
        </button>
    );
}; 